import { DRAFT_MAX_AGE_DAYS } from '@pams/shared';
import type { GuardReason, SaveState } from './autosave';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE WORDS ON THIS SCREEN.
 *
 *  ⚠️ WRITTEN FOR A CLERK IN A CP OFFICE WHO HAS NEVER SEEN THIS SYSTEM.
 *
 *  They do this job on paper and in Excel today. They have never heard the words
 *  "deployment", "slot", "allocation", "detached", "stamp", "policy version" or "fan-out",
 *  and none of those appears below. Where the screen has to name something, it names it in
 *  the words the department already uses: a duty, a shift, a place, a division.
 *
 *  Three rules, taken from `docs/design/DESIGN_LANGUAGE.md` §6c and from what P2 learned
 *  writing the post-type guidance:
 *
 *    1. ONE SHORT SENTENCE. Two only when the second is a consequence the operator cannot
 *       see for themselves. Never three.
 *    2. SAY WHAT HAPPENS, NOT WHAT THE FIELD IS. "Each place becomes its own duty" beats
 *       "sets the location mode to concurrent", which restates the label and helps nobody.
 *    3. IT GOES WHERE THE MISTAKE IS MADE, not where it is discovered.
 *
 *  ⚠️ AND IT LIVES IN ONE MODULE BECAUSE IT IS TESTED. `ArrangementBuilder.test.tsx`
 *  asserts these strings are on screen. Prose with nothing asserting it is prose that gets
 *  deleted in a tidy-up — which is exactly what happened to the guidance P1b wrote.
 *
 *  A tooltip explains a CONTROL. `HELP_PANEL` explains the JOB. They are not
 *  interchangeable and neither should be doing the other's work.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ RE-EXPORTED, NOT REDEFINED. The control-level copy moved to `components/intake/copy`
 * when the round-the-clock form started sharing those controls; it is re-exported here so
 * that every existing import keeps working and there is still exactly one place a reader
 * has to look for "what does this page say".
 */
export {
  BASICS_COPY,
  COMMAND_COPY,
  ENDS_COPY,
  FORCE_COPY,
  PLACES_COPY,
  SHIFT_COPY,
  TIMING_COPY,
} from '@/components/intake/copy';

export const PUBLISH_COPY = {
  publish: 'Creates the duties and asks each division for the officers you have allocated to them.',
  /* [P4-UX] What a refused Publish says, instead of the first blocker in a native title. */
  blockedTooltip: 'Publishing is blocked. Select to see what is still missing.',
  saveFirst: 'Save the draft before publishing.',

  /** ⚠️ Names exactly what will be created, and who will be asked. Never a bare "Are you sure?". */
  confirmTitle: 'Publish this arrangement?',
  /* ⚠️ [amended C2a §4a] `confirmIntro` and `confirmDivisions` are GONE, not commented out.
     The confirmation is a plain "are you sure" by a client decision, so they had zero call
     sites — and §9's rule is that a thing with none is removed rather than kept "for later".
     The publish bar still states what will be created, through `willCreateSummary`. */
  confirmIrreversible: 'The dates and places cannot be changed afterwards.',

  blockedIntro: 'This cannot be published yet:',
  /**
   * ⚠️ [C1f §7e] AFTER PUBLISH THE ONE CONTROL LEADS TO THE NAMING SCREEN.
   *
   * Sentence case, like every other action in this tree (`Add duty point`, `Take back to
   * draft`, `Start a copy`). The brief writes `Assign Names`; this is a deliberate departure,
   * for one capitalisation vocabulary rather than two.
   */
  assignNames: 'Assign names',
  assignNamesTooltip: 'Go to the order and name the officers your office is filling.',
} as const;

export const AUTOSAVE_COPY = {
  /**
   * ⚠️ [amended C1f §6] AUTOSAVE IS BACK, so this is the gap between a keystroke and the blur
   * that commits it rather than a standing state. `autosave.ts` carries the reversal and the
   * mechanism that killed [P3b.3]'s first attempt.
   */
  unsaved: 'Not saved',
  saving: 'Saving…',
  saved: 'Saved',
  /** ⚠️ Says what to DO. A failed save that only says "Error" loses an operator's work. */
  failed: 'Not saved — check your connection and try again',
  /**
   * ⚠️ [C1f §6] THE ARMING GATE SAID NO, AND THIS IS THE ONLY CHANNEL THAT SAYS SO. There is
   * no Save button left to warn from, and a toast on every blur would be noise on a form
   * whose date the operator has simply not typed yet.
   */
  needsDate: 'Not saved — set the order date',
  /**
   * ⚠️ [amended C1f §6] TWO SENTENCES, BECAUSE THE GUARD NOW FIRES FOR TWO REASONS AND ONLY
   * ONE OF THEM IS THE OPERATOR'S TO FIX. Autosave handles an ordinary exit, so what is left
   * is a form autosave CANNOT save.
   */
  guardNoDate: 'This order has no date, so it has not been saved. Leave anyway?',
  guardFailed: 'The last save did not go through. Leave anyway?',
  /** ⚠️ [AUTO-SAVE] The third reason — `guardReasonOf` in `autosave.ts` says when each one is said. */
  guardUnsaved: 'You have changes that have not been saved. Leave anyway?',
} as const;

/** [AUTO-SAVE] The leave prompt's title, by reason — exhaustive, so a fourth reason is a compile error. */
export const GUARD_TITLE: Readonly<Record<GuardReason, string>> = {
  'needs-date': AUTOSAVE_COPY.guardNoDate,
  failed: AUTOSAVE_COPY.guardFailed,
  unsaved: AUTOSAVE_COPY.guardUnsaved,
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.5 §2a] IT ARCHIVES. IT DOES NOT DELETE, AND THE WORDS MAY NOT SAY IT DOES.
 *
 *  `POST /arrangements/:id/archive` writes `archived_at`, `archived_from_status` and — for a
 *  DRAFT only — a `purge_at` thirty days out. Archiving destroys nothing, so "deleted
 *  permanently" would be false of the ACT, and the confirmation says restorable instead.
 *  ⚠️ [amended C5c §8] "RESTORE IT FROM THERE FOR THE NEXT 30 DAYS" IS NOW A PROMISE THAT IS
 *  KEPT. For eleven passes nothing acted on `purge_at` — `purgePlan.util.ts` said so in its own
 *  header — and the thirty days were a window with no end. The duty-lifecycle tick now deletes an
 *  archived draft when its `purge_at` passes, so the sentence below is true in both halves.
 *
 *  ⚠️ TWO BODIES, BECAUSE THERE ARE TWO ACTS. With a saved record there is something to
 *  archive and something to restore from; with none there is only a form to close, and
 *  saying "it moves to the Archive" would be false.
 *
 *  ⚠️ THIS REPLACES `DELETE_COPY`, WHICH WAS DEAD AND WOULD HAVE BEEN WRONG. It had no
 *  importer at all, its title read "Delete this arrangement?" — the promise §2a forbids —
 *  and its `affectsOfficers`/`blocked` lines describe the PUBLISHED cancellation, which is
 *  `pages/ArrangementDetail`'s act and already has its own words in that page's `copy.ts`.
 *  Two vocabularies for one action is what this removes rather than duplicates.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DISCARD_COPY = {
  action: 'Discard',
  title: 'Discard this draft?',
  saved: 'It moves to the Archive. You can restore it from there for the next 30 days.',
  unsaved: 'Nothing has been saved yet, so this closes the form and keeps nothing.',
  okText: 'Discard',
  tooltip: 'Moves this draft to the Archive, where you can restore it for 30 days.',
} as const;

/**
 * The "?" in the page header. It explains the JOB, not a control.
 *
 * Roughly 150 words, in four short paragraphs, because a clerk opening this is looking for
 * orientation rather than reference. It answers the two questions people actually ask on
 * their first day: what am I making, and what happens when I press the button.
 */
const HELP_ORIENTATION = [
  'A police arrangement is one order. It covers the places to be manned, the dates and ' +
    'times they are manned for, how much force is needed, and who is in command.',
  'You do not have to decide what kind of arrangement it is. Fill in what you know and ' +
    'the form grows to match — add a second place and it asks whether officers are needed ' +
    'at both at once; add a second shift and it shows you each one.',
  'The system turns the order into individual duties: one for each place, on each shift, ' +
    'on each date. A three-day arrangement at two places with two shifts a day is twelve ' +
    'duties, all created together.',
] as const;

/** ⚠️ [AUTO-SAVE] The one sentence that differs by mode is how the work is kept, so it is the parameter. */
const helpPublishParagraph = (howKept: string): string =>
  'When you publish, those duties are created and each division you allocated force to is ' +
  'asked to name the officers it is sending. Until then it stays a draft that only your ' +
  `office can see, and ${howKept}. ` +
  /* ⚠️ [C5c §8] Said here, before the day it happens — the client's rule counts from the start. */
  `A draft that is not published within ${String(DRAFT_MAX_AGE_DAYS)} days of being started is deleted.`;

export const HELP_PANEL = {
  triggerLabel: 'What is an arrangement?',
  title: 'What you are making',
  paragraphs: [...HELP_ORIENTATION, helpPublishParagraph('your work is saved as you type')],
  /**
   * ⚠️ [AUTO-SAVE] `ENABLE_AUTO_SAVE=false`. "Saved as you type" would be false the moment the switch
   * is thrown, and this panel is where a first-day clerk learns how the page keeps their work.
   */
  manualSaveParagraphs: [...HELP_ORIENTATION, helpPublishParagraph('your work is kept when you press Save')],
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5c §8] A LIVE DRAFT IS DELETED THIRTY DAYS AFTER IT WAS STARTED — EDITED OR NOT.
 *
 *  `notice` is shown in the last week (`draftExpiry.ts`), with the IST date, beside the save
 *  indicator. `imminent` is the draft past its thirty days that the next sweep will take. `gone`
 *  is what a save that found it deleted says — and ONLY when the draft's own clock says it is past
 *  due, because a 404 also follows a scope change and the API collection's rule is that a 404 is
 *  never rendered as "deleted" on a guess.
 *
 *  ⚠️ "STARTED", NEVER "CREATED": an order taken back to draft counts from the un-publish, and an
 *  operator told "created 31 days ago" about an order they un-published last week would be told
 *  something false.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DRAFT_EXPIRY_COPY = {
  notice: (date: string): string => `Unless it is published, this draft will be deleted on ${date}.`,
  imminent: `This draft was started more than ${String(DRAFT_MAX_AGE_DAYS)} days ago and will be deleted shortly unless it is published.`,
  gone: `This draft no longer exists. It was not published within ${String(DRAFT_MAX_AGE_DAYS)} days of being started, so it was deleted.`,
} as const;

/**
 * ⚠️ THE THREE STEPS, AND WHICH SECTIONS LIVE IN EACH.
 *
 * The intake is still ONE form — every field stays registered and every value stays in the
 * store no matter which step is showing (see the page's note on why they are hidden with a
 * class rather than unmounted). What the steps buy is a shorter column: six stacked cards
 * on a laptop meant the force grid, the densest thing on the screen, was reached by
 * scrolling past everything else.
 *
 * Every step is reachable at any time by clicking it, and the right-hand checklist jumps
 * ACROSS steps. Nothing is gated behind finishing the one before it — that is what separates
 * this from the wizard the brief rejected, where a field you have already filled becomes
 * something you have to go back and hunt for.
 *
 * ⚠️ WHICH SECTIONS SIT ON A STEP IS NOT WRITTEN HERE. Each descriptor in `sections.tsx`
 * names its own `step`, so a section moves in one place rather than in two that can
 * disagree — and `StepKey` is derived from this tuple, so deleting a step below is a
 * compile error until every section that named it has been repointed.
 */
/**
 * ⚠️ [amended D4] TWO TABS, NOT THREE STEPS — and this tuple IS the change.
 *
 * `IntakeForm` already equates a tab with a step (`isSectionHidden` compares
 * `stepIndexOf(steps, d.step) !== currentStep`), so §5's two tabs are a two-entry tuple and
 * nothing else. Deleting `what`/`force`/`orders` is a COMPILE ERROR until every descriptor
 * that named one has been repointed, which is exactly what `StepKey` is derived for.
 */
export const FORM_STEPS = [
  { key: 'pa', title: 'PA details' },
  { key: 'duty', title: 'Assign duty' },
] as const;

export const STEP_COPY = {
  /** [D4.1] The page-header control that opens the checklist and the order preview. */
  summaryToggle: 'Summary',
  next: 'Next',
  back: 'Back',
  warningPill: 'Cannot publish yet',
  warningPillTooltip: 'Some things still have to be filled in before this order can be published. Click to see them.',
  blockedTitle: 'This cannot be published yet',
  noticeTitle: 'Worth knowing before you publish',
  noticeIntro: 'You can publish anyway. These are things somebody will have to come back to:',
  reviewLabel: 'Review',
} as const;

/**
 * ⚠️ [amended D4] THREE SECTIONS, NOT SIX. `places`, `timing`, `command` and `force` are no
 * longer sections of the PAGE — they are parts of a CARD, and their components moved down
 * unchanged rather than being rewritten.
 */
export const SECTION_TITLES = {
  /** ⚠️ [C3a §3] The PA's own paperwork, on tab 1 beside the header fields. */
  attachments: 'Files',
  basics: 'PA details',
  dutyPoints: 'Duty points',
  /* ⚠️ [D4.3] "General", because each duty point now has instructions of its own. */
  extras: 'General instructions and supporting arrangements',
} as const;

/** The words the duty point stack owns. [amended D4] */
/**
 * "A", "A and B", "A, B and C". A list a person reads, not a join on commas.
 */
const listOf = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${String(items[items.length - 1] ?? '')}`;

export const DUTY_COPY = {
  /** [C4a §4] A duty point's zone — free text, suggested from this order's other duty points. */
  zone: 'Zone',
  zonePlaceholder: 'Optional',
  /** [C5c §4] A duty point's sector — the zone's twin, beside it, and of any length. */
  sector: 'Sector',
  sectorPlaceholder: 'Optional',
  /**
   * ⚠️ [C3c §9b] IT NAMES WHAT IS LOCKED AND WHAT IS NOT, because the card is only PARTLY frozen
   * and a sentence that said "this order cannot be edited" would be false — the force counts,
   * the command seats and the per-night overrides are all still open, which is FR-5.2.13 and is
   * what the server allows.
   */
  structureLocked:
    'Officers have been named on this order, so a duty point\u2019s place, dates and policy can no longer be changed. Force numbers, command roles and per-night overrides are still editable.',
  /* [STAND-DOWN] Renamed on instruction, UI only — the entity is still a duty point everywhere else. */
  add: 'Add reporting place',
  /**
   * ⚠️ [D4.3] THE CARD'S OWN INSTRUCTIONS, AND THE HEADING SAYS WHOSE.
   *
   * "Instructions" alone, under a card, beside a tab-1 panel also called "Instructions", is
   * two controls with one name. This one names the place; the general one names the order.
   */
  instructions: 'Instructions for this duty point',
  empty: 'A PA is a list of duty points. Add the first one.',
  duplicate: 'Duplicate',
  duplicateHint:
    'Copies the times, the policy and the whole force grid. Only the place is left blank, because two duty points at one place with one schedule are one duty point.',
  remove: 'Remove',
  where: 'Where',
  /** [D4.2] The field's own label, now that the `Where` heading above it is gone. */
  dutyPlace: 'Duty place',
  reportingPlace: 'Reporting place',
  reportingTime: 'Reporting time',
  when: 'When',
  policy: 'Policy',
  force: 'Force required',
  command: 'Command',
  sourced: (sourced: number, asked: number) => `${String(sourced)} of ${String(asked)} sourced`,
  /**
   * ⚠️ The one sentence about the fork, said where the column set is edited.
   *
   * ⚠️ [amended C1a] REWRITTEN, BECAUSE IT WAS WRONG TWICE.
   *
   * It PROMISED A FORK THE WRITE PATH DOES NOT PERFORM: `policyForks` is hard-coded `[]`
   * in `arrangement.service.ts` ("EMPTY UNTIL §5c'S INLINE FORK LANDS ON THE WRITE PATH")
   * and `resolveDutyPointPolicies` never creates a version. Saving does not touch the
   * policy at all, so "creates a new version of the policy" described something that has
   * never happened.
   *
   * And it read as "YOU changed the policy" when, after C1a §2, the likelier cause is that
   * the policy changed underneath the card. That half is answered structurally rather than
   * in words: `versionMoved` has its own sentence and suppresses this one, so each fact is
   * stated once by whichever notice actually applies.
   *
   * It NAMES the columns, because the header of such a column may itself render `—` and a
   * warning about an unnamed thing is unactionable.
   */
  forkNotice: (columns: readonly string[]): string =>
    `This duty point asks for ${columns.length > 0 ? listOf(columns) : 'a column'} , which its policy version does not carry. That applies to this order only — the policy itself is not changed.`.replace(
      ' ,',
      ',',
    ),

  /**
   * ⚠️ [C1a §2] THE POLICY MOVED UNDER AN UNPINNED CARD.
   *
   * Both directions are stated, and so is the cost of each choice: what updating destroys,
   * and what ignoring it leaves behind. An operator should never have to click to find out
   * which. `PolicyBuilder`'s retired-column offer is the precedent — "the button says
   * exactly how many and does nothing until it is pressed".
   */
  versionMoved: {
    title: (label: string): string => `The policy has changed. This duty point will be saved against ${label}.`,
    body: (label: string, added: readonly string[], dropped: readonly string[]): string => {
      const parts: string[] = [];
      if (added.length > 0) parts.push(`${label} adds ${listOf(added)}`);
      if (dropped.length > 0) parts.push(`${label} no longer has ${listOf(dropped)}`);
      const change = parts.join(' and ');
      const cost =
        dropped.length > 0
          ? ` Updating removes ${dropped.length === 1 ? 'that column' : 'those columns'} and anything typed into ${dropped.length === 1 ? 'it' : 'them'}; every other number you have typed is kept.`
          : ' Updating keeps every number you have typed.';
      const ignored =
        dropped.length > 0
          ? ` If you leave it, this duty point will be issued asking for ${dropped.length === 1 ? 'a column' : 'columns'} ${label} does not have.`
          : '';
      return `${change}.${cost}${ignored}`;
    },
    action: 'Update the columns',
  },
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C3a §7] THE FALLBACK SAVE — AND ITS NAME IS `Save`, NOT `Save draft`.
 *
 *  ⚠️ [C1f §7] DELETED `Save draft` DELIBERATELY, and §7b is explicit that this does not restore
 *  it: that was a PERMANENT control autosave made redundant, and this appears only while a write
 *  is owed. Reusing the old words would make it read as the old button coming back, which is the
 *  one reading §7b forbids.
 *
 *  ⚠️ THE LABEL NAMES THE AXIS AND THE `title` NAMES THE STATE — [C1e §6b]'s rule. "Save" is what
 *  the button does in every one of the four states below; WHY it is showing differs, and the
 *  operator only needs that when they wonder.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const SAVE_COPY = {
  action: 'Save',
  /**
   * ⚠️ [AUTO-SAVE] `issued` IS REQUIRED. With autosave off this button also stands on an ISSUED
   * order (`publishControl.ts`), where "so this order can be published" is false and the slot swaps
   * back to `Assign names` rather than to Publish — and a default would say the false thing silently.
   */
  tooltip: (state: SaveState, issued: boolean): string =>
    state === 'failed'
      ? 'The last save did not go through. Try again.'
      : state === 'needs-date'
        ? 'Set the order date, then save.'
        : state === 'saving'
          ? /* ⚠️ [C3c §10] It NAMES THE NEXT STEP. This slot swaps to Publish when the save
               lands, and an operator who has just been refused a click needs to be told that
               rather than left to discover it by clicking again — which is what they did. */
            `Saving… ${issued ? PUBLISH_COPY.assignNames : 'Publish'} becomes available when this finishes.`
          : state === 'idle'
            ? 'Nothing to save yet.'
            : issued
              ? 'Save your changes to this order.'
              : 'Save your changes so this order can be published.',
} as const;

/**
 * ⚠️ [C3a §3] THE FILES SECTION'S OWN DETAIL LINE. It says how many are WAITING, because a
 * queued file is work the server has not seen and the checklist is where an operator looks to
 * find out what is outstanding.
 */
export const ATTACHMENT_SECTION_COPY = {
  queued: (count: number): string => `${String(count)} waiting to upload`,
  /**
   * ⚠️ [C3a §3] A PUBLISH BLOCKER, AND IT NAMES THE COST RATHER THAN THE RULE. Publishing
   * navigates away, which kills the drain — so a file still waiting is a file that will not
   * arrive. It clears itself within a moment, so the sentence says "wait", not "fix".
   */
  blocked: (count: number): string =>
    count === 1
      ? 'A file is still uploading — it will be ready in a moment'
      : `${String(count)} files are still uploading`,
  /**
   * ⚠️ [AUTO-SAVE] WITH AUTOSAVE OFF, A FILE CHOSEN BEFORE THE FIRST SAVE WAITS FOR THE SAVE BUTTON —
   * nothing is uploading, and "it will be ready in a moment" would be false for as long as nobody
   * presses it. Both lines name the act that sends it.
   */
  queuedForSave: (count: number): string => `${String(count)} waiting for Save`,
  waitingForSave: (count: number): string =>
    count === 1 ? 'A file is waiting — press Save to upload it' : `${String(count)} files are waiting — press Save`,
} as const;

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C5c §2] ONE PERSON, MANY DUTY POINTS — THE SHARED-COLUMN WORDS.
 *
 *  ⚠️ EVERY CONFIRMATION SAYS WHAT HAPPENS TO NUMBERS SOMEBODY TYPED, WITH THE NUMBERS. A bare
 *  "Share this column?" hides that the other cards' own counts are set aside, and that a card on
 *  another policy loses columns — [C1a §2]'s "the button says exactly how many", one feature over.
 *
 *  ⚠️ "DP1" IS THE CARD'S POSITION ON THIS ORDER, the same ordinal the record, the preview and
 *  the printed sheet print (`dutyPointDisplayTitle`), so the sentence names what the operator
 *  will read on paper.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const SHARE_COPY = {
  enableTitle: (column: string, anchor: string): string => `Ask for ${column} once, on ${anchor}?`,
  enableIntro: (column: string, anchor: string): string =>
    `The ${column} named on ${anchor} will cover every duty point on this order. The other duty points will not ask for their own.`,
  /** One line per other card that had its own number for the column. */
  setAside: (card: string, count: number): string =>
    `${card}: its own ${String(count)} is set aside, and comes back if you stop sharing.`,
  converged: (card: string, policy: string): string =>
    `${card} moves onto ${policy}, the policy of the duty point the column is shared from.`,
  dropped: (card: string, columns: readonly { name: string; requiredCount: number }[]): string =>
    `${card} loses ${columns.map((row) => `${row.name} (${String(row.requiredCount)})`).join(', ')}, which that policy does not have.`,
  nothingLost: 'Nothing typed is lost.',
  enableOk: 'Share the column',
  /** §2f — refused outright, with the count, when converging would drop named supervisors. */
  seatsTitle: 'Clear the command seats first',
  /**
   * ⚠️ [C6a §2] A ROLE ASKED OF A DIVISION IS STATED BY ITS LABEL, NEVER COUNTED — "DP2 asks a
   * division for In-charge". With no ask anywhere the sentence is byte for byte what it was.
   */
  seatsBody: (rows: readonly { card: string; count: number; asked?: readonly string[] | undefined }[]): string => {
    const named = rows.filter((row) => row.count > 0);
    const asking = rows.filter((row) => row.asked?.[0] !== undefined);
    const clauses = [
      ...named.map(
        (row) =>
          `${row.card} has ${String(row.count)} officer${row.count === 1 ? '' : 's'} named to a command seat the shared policy would not keep`,
      ),
      ...asking.map(
        (row) =>
          `${row.card} asks a division for ${(row.asked ?? []).join(', ')}, which the shared policy does not have`,
      ),
    ];
    const remove = [
      ...(named[0] === undefined ? [] : [named.length === 1 && named[0].count === 1 ? 'that name' : 'those names']),
      ...(asking[0] === undefined
        ? []
        : [asking.length === 1 && asking[0].asked?.[1] === undefined ? 'that ask' : 'those asks']),
    ].join(' and ');
    return `${clauses.join('; ')}. Remove ${remove}, or put every duty point on one policy, before sharing.`;
  },
  seatsUnchecked: 'The rank list could not be loaded, so the command seats could not be checked. Try again.',
  disableTitle: (column: string): string => `Stop sharing ${column}?`,
  disableBody: (restored: number): string =>
    restored > 0
      ? `Each other duty point gets its own number for this column back — ${String(restored)} ${restored === 1 ? 'number' : 'numbers'} set aside when sharing began.`
      : 'Each other duty point gets this column back, at zero.',
  disableOk: 'Stop sharing',
  removeAnchorTitle: 'Remove this duty point?',
  removeAnchorBody: (columns: readonly string[]): string =>
    `${columns.join(', ')} ${columns.length === 1 ? 'is' : 'are'} shared from this duty point. Removing it stops sharing, and every other duty point gets its own number back.`,
  removeAnchorOk: 'Remove',
  versionDropsTitle: 'The new version drops a shared column',
  versionDropsBody: (rows: readonly { column: string; setAside: number }[]): string =>
    `${rows
      .map((row) => `${row.column} (${String(row.setAside)} set aside on the other duty points)`)
      .join(
        ', ',
      )} ${rows.length === 1 ? 'is' : 'are'} not on the current version. Updating stops sharing ${rows.length === 1 ? 'it' : 'them'}, and the numbers set aside are not kept.`,
  versionDropsOk: 'Update the columns',
  /** Beside the policy picker while any column is shared. */
  policyLocked:
    'A column is shared across the duty points, so every duty point uses the same policy. Stop sharing to change it.',
  /** A one-off column cannot be added on a card the same column already covers. */
  adHocCovered: (column: string, anchor: string): string =>
    `${column} is already shared from ${anchor}, so it cannot also be asked for here.`,
} as const;
